import { useState, useEffect } from 'react';
import { Tag, Modal, Spin, message, Tooltip, Empty } from 'antd';
import { User, Box, CheckCircle, ArrowDown, ArrowUp, RefreshCw } from 'react-feather';
import moment from 'moment';
import { GetDailyRoomStatusApi } from '../../../utils/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format a raw occupant object into a display-ready shape. */
function formatOcc(occ) {
    return {
        guest: occ.name,
        checkIn: moment(occ.admissionDate).format('DD MMM'),
        checkOut: moment(occ.dischargeDate).format('DD MMM'),
        gender: occ.gender,
        occupancyMode: occ.occupancyMode,
        bookingStatus: occ.status,
    };
}

/**
 * Assign each occupant to a bed slot for a given date.
 *
 * Occupant categories (relative to `selectedDate`):
 *   staying  – admit BEFORE today AND discharge AFTER today (pure mid-stay)
 *   outgoing – dischargeDate === today  (checking out)
 *   incoming – admissionDate === today  (checking in)
 *
 * When an outgoing + incoming share the same bed slot → Turnover.
 *
 * Returns an array of bed objects (length === capacity):
 *   { id, status: 'Available' }
 *   { id, status: 'Occupied', guest, checkIn, checkOut, gender, … }
 *   { id, status: 'Turnover', outgoing: {…}, incoming: {…} }
 *   { id, status: 'Blocked',  guest: string }
 */
function buildBeds(room, selectedDate, isBlockedByMixedGender, roomStatus) {
    const capacity = room.capacity;
    const occupants = room.occupants || [];
    const dateStr = selectedDate; // 'YYYY-MM-DD'

    // ── Classify ─────────────────────────────────────────────────────────────
    const staying = occupants.filter(o => {
        const admit = moment(o.admissionDate).format('YYYY-MM-DD');
        const discharge = moment(o.dischargeDate).format('YYYY-MM-DD');
        // Strictly before today AND strictly after today — pure mid-stay.
        // Arrivals today (admit === dateStr) are NOT staying; they are incoming.
        return admit < dateStr && discharge > dateStr;
    });

    const outgoing = occupants.filter(o =>
        moment(o.dischargeDate).format('YYYY-MM-DD') === dateStr
    );

    const incoming = occupants.filter(o =>
        moment(o.admissionDate).format('YYYY-MM-DD') === dateStr
    );

    // ── Assign beds iteratively ───────────────────────────────────────────────
    const beds = [];
    let bedIdx = 0;

    // 1. Staying occupants get their own beds first
    for (const occ of staying) {
        if (bedIdx >= capacity) break;
        beds.push({
            id: String.fromCharCode(65 + bedIdx),
            status: 'Occupied',
            ...formatOcc(occ),
        });
        bedIdx++;
    }

    // 2. Pair outgoing[i] + incoming[i] into the same bed slot (turnover)
    const pairCount = Math.max(outgoing.length, incoming.length);
    for (let i = 0; i < pairCount; i++) {
        if (bedIdx >= capacity) break;
        const out = outgoing[i];
        const inc = incoming[i];
        const id = String.fromCharCode(65 + bedIdx);

        if (out && inc) {
            // TURNOVER — same bed, checkout + check-in on the same day
            beds.push({ id, status: 'Turnover', outgoing: formatOcc(out), incoming: formatOcc(inc) });
        } else if (out) {
            beds.push({ id, status: 'Occupied', ...formatOcc(out) });
        } else if (inc) {
            beds.push({ id, status: 'Occupied', ...formatOcc(inc) });
        }
        bedIdx++;
    }

    // 3. Fill the remaining beds (Available or Blocked)
    while (bedIdx < capacity) {
        const id = String.fromCharCode(65 + bedIdx);
        if (isBlockedByMixedGender) {
            beds.push({ id, status: 'Blocked', guest: 'Blocked' });
        } else if (roomStatus === 'Full') {
            // Room is Full but this bed slot has no occupant data —
            // happens with NON-SHARED mode where 1 guest reserves the whole room.
            beds.push({ id, status: 'Blocked', guest: 'Reserved (Non-Shared)' });
        } else {
            beds.push({ id, status: 'Available' });
        }
        bedIdx++;
    }

    return beds;
}

// ─── Room Card ────────────────────────────────────────────────────────────────

const RoomCard = ({ room, onClick }) => {
    const hasTurnover = room.beds && room.beds.some(b => b.status === 'Turnover');
    const isFull = room.status === 'Full';
    const isShared = room.status === 'Shared' || (room.occupied > 0 && room.occupied < room.capacity);
    const isEmpty = !isFull && !isShared;

    const colors = isFull
        ? { border: '#fca5a5', bg: '#fff5f5', text: '#dc2626', icon: '#ef4444', badge: '#fee2e2', badgeText: '#991b1b' }
        : isShared
            ? { border: '#fcd34d', bg: '#fffbeb', text: '#d97706', icon: '#f59e0b', badge: '#fef3c7', badgeText: '#92400e' }
            : { border: '#6ee7b7', bg: '#f0fdf4', text: '#059669', icon: '#10b981', badge: '#d1fae5', badgeText: '#065f46' };

    const label = isFull ? 'Full' : isShared ? 'Shared' : 'Available';

    const isLargeWard = room.capacity > 6;
    const pct = room.capacity > 0 ? (room.occupied / room.capacity) * 100 : 0;

    return (
        <Tooltip
            title={`${room.occupied}/${room.capacity} beds occupied${hasTurnover ? ' · Turnover today' : ''}`}
            placement="top"
        >
            <div
                onClick={onClick}
                style={{
                    border: `1px solid ${hasTurnover ? '#a78bfa' : colors.border}`,
                    borderRadius: '14px',
                    padding: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    cursor: 'pointer',
                    backgroundColor: hasTurnover ? '#f5f3ff' : colors.bg,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    width: '130px',
                    height: '120px',
                    position: 'relative',
                    overflow: 'hidden'
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px -4px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.05)';
                }}
            >
                {/* Turnover indicator badge (top-right) */}
                {hasTurnover && (
                    <div style={{
                        position: 'absolute', top: 6, right: 6,
                        background: '#8b5cf6', borderRadius: '50%',
                        width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <RefreshCw size={9} color="#fff" />
                    </div>
                )}

                {/* Room Number */}
                <div style={{
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    color: hasTurnover ? '#6d28d9' : colors.text,
                    width: '100%',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {room.roomNo}
                </div>

                {/* Bed Indicator */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    {isLargeWard ? (
                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                            <div style={{
                                width: '80%', height: '5px', borderRadius: '99px',
                                background: 'rgba(0,0,0,0.08)', overflow: 'hidden',
                            }}>
                                <div style={{
                                    height: '100%',
                                    width: `${pct}%`,
                                    borderRadius: '99px',
                                    background: hasTurnover ? '#8b5cf6' : colors.icon,
                                    transition: 'width 0.4s ease',
                                }} />
                            </div>
                            <div style={{ fontSize: '0.7rem', color: hasTurnover ? '#6d28d9' : colors.text, fontWeight: 700 }}>
                                {room.occupied}/{room.capacity}
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', justifyContent: 'center', alignContent: 'center' }}>
                            {Array.from({ length: room.capacity }).map((_, idx) => {
                                const bed = room.beds && room.beds[idx];
                                const isTurnoverBed = bed?.status === 'Turnover';
                                const isOccupiedBed = bed?.status === 'Occupied' || isTurnoverBed;
                                return isTurnoverBed ? (
                                    <RefreshCw
                                        key={idx}
                                        size={15}
                                        color="#8b5cf6"
                                        strokeWidth={2.5}
                                    />
                                ) : (
                                    <User
                                        key={idx}
                                        size={15}
                                        fill={isOccupiedBed ? colors.icon : 'none'}
                                        color={isOccupiedBed ? colors.icon : '#cbd5e1'}
                                        strokeWidth={2.5}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Status Badge */}
                <div style={{
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: hasTurnover ? '#5b21b6' : colors.badgeText,
                    background: hasTurnover ? '#ede9fe' : colors.badge,
                    borderRadius: '6px',
                    padding: '3px 10px',
                    whiteSpace: 'nowrap',
                }}>
                    {hasTurnover ? 'Turnover' : label}
                </div>
            </div>
        </Tooltip>
    );
};


// ─── Bed Card ─────────────────────────────────────────────────────────────────

const OccupantInfo = ({ data, isOutgoing }) => (
    <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '14px 12px',
        background: isOutgoing ? '#fef2f2' : '#f0fdf4',
        borderRadius: '12px',
        border: `1px solid ${isOutgoing ? '#fecaca' : '#bbf7d0'}`,
        gap: '6px',
        flex: 1,
    }}>
        <div style={{
            width: 36, height: 36,
            background: isOutgoing ? '#fee2e2' : '#dcfce7',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            {isOutgoing
                ? <ArrowUp size={18} color="#dc2626" strokeWidth={2.5} />
                : <ArrowDown size={18} color="#16a34a" strokeWidth={2.5} />
            }
        </div>
        <div style={{
            fontSize: '0.62rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            color: isOutgoing ? '#dc2626' : '#16a34a',
        }}>
            {isOutgoing ? 'Checking Out' : 'Checking In'}
        </div>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', textAlign: 'center', lineHeight: 1.3 }}>
            {data.guest}
        </div>
        {data.gender && (
            <span style={{
                fontSize: '0.68rem',
                background: data.gender === 'Female' ? '#fce7f3' : '#dbeafe',
                color: data.gender === 'Female' ? '#be185d' : '#1e40af',
                padding: '2px 8px', borderRadius: '20px', fontWeight: 600,
            }}>
                {data.gender}
            </span>
        )}
        <div style={{
            display: 'flex', gap: '8px', marginTop: 4,
            fontSize: '0.72rem', color: '#64748b', fontWeight: 600,
        }}>
            <span>{data.checkIn}</span>
            <span style={{ color: '#cbd5e1' }}>→</span>
            <span>{data.checkOut}</span>
        </div>
    </div>
);

const BedCard = ({ bed }) => {
    const isOccupied = bed.status === 'Occupied';
    const isTurnover = bed.status === 'Turnover';
    const isBlocked = bed.status === 'Blocked';
    const isAvailable = bed.status === 'Available';

    // ── Turnover state ────────────────────────────────────────────────────────
    if (isTurnover) {
        return (
            <div style={{
                border: '1px solid #c4b5fd',
                borderRadius: '16px',
                padding: '20px 18px',
                flex: 1,
                backgroundColor: '#faf5ff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.12)',
                minWidth: '240px',
                maxWidth: '320px',
                transition: 'all 0.2s ease',
                gap: '14px',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', justifyContent: 'center' }}>
                    <div style={{
                        width: 36, height: 36, background: '#ede9fe', borderRadius: '10px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <RefreshCw size={18} color="#7c3aed" />
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: '0.75rem', color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            Bed {bed.id} — Turnover
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#a78bfa' }}>Same-day checkout &amp; check-in</div>
                    </div>
                </div>

                {/* Divider */}
                <div style={{ width: '100%', height: '1px', background: '#e9d5ff' }} />

                {/* Outgoing + Incoming */}
                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                    <OccupantInfo data={bed.outgoing} isOutgoing={true} />
                    <OccupantInfo data={bed.incoming} isOutgoing={false} />
                </div>
            </div>
        );
    }

    // ── Regular Occupied / Available / Blocked ─────────────────────────────────
    return (
        <div style={{
            border: `1px solid ${isOccupied ? '#93c5fd' : isBlocked ? '#d8b4fe' : '#e2e8f0'}`,
            borderRadius: '16px',
            padding: '24px',
            flex: 1,
            backgroundColor: isOccupied ? '#eff6ff' : isBlocked ? '#faf5ff' : '#ffffff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: isOccupied
                ? '0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06)'
                : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            minWidth: '220px',
            maxWidth: '300px',
            transition: 'all 0.2s ease',
            position: 'relative',
        }}>
            {/* Bed Illustration */}
            <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: isOccupied ? '#3b82f6' : isBlocked ? '#8b5cf6' : '#f1f5f9',
                borderRadius: '16px',
                marginBottom: '16px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isOccupied ? '0 10px 15px -3px rgba(59, 130, 246, 0.3)' : 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
            }}>
                {/* Pillow */}
                <div style={{
                    position: 'absolute', top: 12, width: '42px', height: '14px',
                    backgroundColor: isOccupied ? 'rgba(255,255,255,0.4)' : '#cbd5e1', borderRadius: '4px',
                }} />
                {isOccupied
                    ? <User size={40} color="#fff" strokeWidth={2} />
                    : isBlocked
                        ? <RefreshCw size={28} color="#fff" strokeWidth={2} />
                        : <div style={{ width: 40, height: 40 }} />
                }
            </div>

            {/* Bed Label */}
            <div style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: '12px',
            }}>
                Bed {bed.id}
            </div>

            {isOccupied ? (
                <>
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', textAlign: 'center', lineHeight: 1.2 }}>
                        {bed.guest || 'Guest'}
                    </h3>
                    {bed.gender && (
                        <span style={{
                            fontSize: '0.75rem',
                            background: bed.gender === 'Female' ? '#fce7f3' : '#dbeafe',
                            color: bed.gender === 'Female' ? '#be185d' : '#1e40af',
                            padding: '4px 10px', borderRadius: '20px', fontWeight: 600, marginBottom: '20px',
                        }}>
                            {bed.gender}
                        </span>
                    )}

                    <div style={{
                        width: '100%', borderTop: '1px solid #bfdbfe', paddingTop: '16px',
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '0.85rem', marginTop: 'auto',
                    }}>
                        <div>
                            <span style={{ display: 'block', color: '#64748b', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Check-in</span>
                            <span style={{ color: '#059669', fontWeight: 700 }}>{bed.checkIn || 'N/A'}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <span style={{ display: 'block', color: '#64748b', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Check-out</span>
                            <span style={{ color: '#dc2626', fontWeight: 700 }}>{bed.checkOut || 'N/A'}</span>
                        </div>
                    </div>
                </>
            ) : isBlocked ? (
                <div style={{ textAlign: 'center', marginTop: '8px', paddingBottom: '16px' }}>
                    <div style={{ color: '#7c3aed', fontWeight: 700, fontSize: '1rem' }}>{bed.guest || 'Blocked'}</div>
                </div>
            ) : (
                <div style={{ textAlign: 'center', marginTop: '8px', paddingBottom: '16px' }}>
                    <div style={{
                        width: '32px', height: '32px', background: '#d1fae5', borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px auto'
                    }}>
                        <CheckCircle size={18} color="#059669" />
                    </div>
                    <div style={{ color: '#059669', fontWeight: 700, fontSize: '1rem' }}>Available</div>
                    <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: '4px' }}>Ready for assignment</div>
                </div>
            )}
        </div>
    );
};


// ─── Legend ───────────────────────────────────────────────────────────────────

const Legend = () => (
    <div style={{ display: 'flex', gap: '24px', alignItems: 'center', background: '#f8fafc', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        {[
            { color: '#10b981', label: 'Available' },
            { color: '#f59e0b', label: 'Shared' },
            { color: '#ef4444', label: 'Full' },
            { color: '#8b5cf6', label: 'Turnover' },
        ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, boxShadow: `0 0 0 2px ${color}33` }} />
                <span style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 500 }}>{label}</span>
            </div>
        ))}
    </div>
);

// ─── Room Layout (Main) ───────────────────────────────────────────────────────

const RoomLayout = ({ dashboardDate }) => {
    const [roomGroups, setRoomGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const currentDate = dashboardDate ? moment(dashboardDate) : moment();

    useEffect(() => {
        fetchDailyStatus();
    }, [dashboardDate]);

    const fetchDailyStatus = async () => {
        setLoading(true);
        try {
            const formattedDate = currentDate.format('YYYY-MM-DD');
            const response = await GetDailyRoomStatusApi({ Query: `?date=${formattedDate}` });

            if (response?.status === 200 && response?.data?.success) {
                const mappedGroups = response.data.data.map(group => ({
                    id: group.roomTypeId,
                    name: group.roomTypeName,
                    rooms: group.rooms.map(room => {
                        let currentStatus = room.status;
                        let isBlockedByMixedGender = false;

                        // rule: SEMI-DELUXE ROOM TYPE with 3 beds, if has both Male and Female occupants,mark as Full
                        if (group.roomTypeCode === 'SEMI-DELUXE ROOM TYPE' && room.capacity === 3) {
                            const occupants = room.occupants || [];
                            const hasMale = occupants.some(occ => occ.gender === 'Male');
                            const hasFemale = occupants.some(occ => occ.gender === 'Female');
                            if (hasMale && hasFemale && room.occupiedCount < room.capacity) {
                                isBlockedByMixedGender = true;
                                currentStatus = 'Full';
                            }
                        }

                        const beds = buildBeds(room, formattedDate, isBlockedByMixedGender, currentStatus);

                        return {
                            id: room.roomId,
                            roomNo: room.roomNo,
                            status: currentStatus,
                            capacity: room.capacity,
                            occupied: room.occupiedCount,
                            beds,
                        };
                    }),
                }));
                setRoomGroups(mappedGroups);
            } else {
                message.error(response?.data?.message || 'Failed to fetch room status');
            }
        } catch (error) {
            console.error(error);
            message.error('Error fetching room status');
        } finally {
            setLoading(false);
        }
    };

    const handleRoomClick = (room, groupName) => {
        setSelectedRoom({ ...room, groupName });
        setIsModalOpen(true);
    };

    // Compute modal width based on number of beds (wider for turnover beds)
    const modalWidth = selectedRoom
        ? Math.max(600, selectedRoom.beds.length * (selectedRoom.beds.some(b => b.status === 'Turnover') ? 340 : 260))
        : 800;

    return (
        <div>
            {/* Summary + Legend Row */}
            {!loading && roomGroups.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <Legend />
                </div>
            )}

            {loading ? (
                <div style={{ textAlign: 'center', padding: '80px 0' }}>
                    <Spin size="large" />
                    <div style={{ marginTop: '16px', color: '#64748b', fontSize: '0.95rem', fontWeight: 500 }}>Loading room status...</div>
                </div>
            ) : roomGroups.length === 0 ? (
                <Empty description={<span style={{ color: '#64748b' }}>No room data available for this date.</span>} style={{ padding: '60px 0' }} />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {roomGroups.map(group => {
                        const occupied = group.rooms.filter(r => r.status === 'Full').length;
                        const total = group.rooms.length;
                        return (
                            <div key={group.id} style={{
                                background: '#fff',
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                overflow: 'hidden',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
                            }}>
                                {/* Group Header */}
                                <div style={{
                                    padding: '16px 24px',
                                    borderBottom: '1px solid #f1f5f9',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    background: '#ffffff',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{
                                            width: 10, height: 10, borderRadius: '50%',
                                            background: occupied === total ? '#ef4444' : occupied > 0 ? '#f59e0b' : '#10b981',
                                            boxShadow: occupied === total ? '0 0 0 2px #fecaca' : occupied > 0 ? '0 0 0 2px #fde68a' : '0 0 0 2px #a7f3d0'
                                        }} />
                                        <span style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1e293b' }}>{group.name}</span>
                                    </div>
                                    <span style={{
                                        fontSize: '0.8rem', fontWeight: 600, color: '#64748b',
                                        background: '#f8fafc', padding: '4px 12px', borderRadius: '99px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        {total} Rooms
                                    </span>
                                </div>

                                {/* Room Cards */}
                                <div style={{ padding: '24px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                    {group.rooms.length > 0 ? group.rooms.map(room => (
                                        <RoomCard key={room.id} room={room} onClick={() => handleRoomClick(room, group.name)} />
                                    )) : (
                                        <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.95rem' }}>No rooms in this category</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Room Detail Modal */}
            <Modal
                title={null}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={Math.min(modalWidth, 1100)}
                centered
                styles={{
                    content: { borderRadius: '24px', padding: 0, overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' },
                    mask: { backdropFilter: 'blur(8px)', background: 'rgba(15, 23, 42, 0.4)' },
                }}
                zIndex={9999}
            >
                {selectedRoom && (
                    <div>
                        {/* Modal Header */}
                        <div style={{
                            padding: '24px 32px',
                            borderBottom: '1px solid #f1f5f9',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: '#ffffff',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    width: 48, height: 48, background: '#eff6ff',
                                    borderRadius: '12px', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', color: '#3b82f6',
                                    boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)',
                                }}>
                                    <Box size={24} strokeWidth={2} />
                                </div>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: '1.25rem', color: '#0f172a' }}>Room {selectedRoom.roomNo}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#64748b', marginTop: '2px', fontWeight: 500 }}>{selectedRoom.groupName}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {selectedRoom.beds.some(b => b.status === 'Turnover') && (
                                    <Tag
                                        style={{
                                            fontSize: '0.78rem', padding: '4px 12px', borderRadius: '99px', fontWeight: 600,
                                            background: '#ede9fe', color: '#6d28d9', border: '1px solid #c4b5fd',
                                        }}
                                    >
                                        <RefreshCw size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                        Turnover Day
                                    </Tag>
                                )}
                                <Tag
                                    color={selectedRoom.status === 'Available' ? 'green' : selectedRoom.status === 'Full' ? 'red' : 'gold'}
                                    style={{
                                        fontSize: '0.85rem', padding: '6px 14px', borderRadius: '99px', fontWeight: 600, border: 'none',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}
                                >
                                    {selectedRoom.status.toUpperCase()}
                                </Tag>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '32px' }}>
                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                    Room Layout &mdash; {currentDate.format('DD MMMM YYYY')}
                                </div>
                                {selectedRoom.beds.some(b => b.status === 'Turnover') && (
                                    <div style={{
                                        marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '8px',
                                        background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px',
                                        padding: '6px 16px', fontSize: '0.8rem', color: '#7c3aed', fontWeight: 600,
                                    }}>
                                        <RefreshCw size={13} />
                                        Some beds have a same-day checkout &amp; check-in (Turnover)
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {selectedRoom.beds.map((bed, idx) => (
                                    <BedCard key={idx} bed={bed} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

// ─── Export ───────────────────────────────────────────────────────────────────

const DailyRoomStatus = ({ dashboardDate }) => (
    <RoomLayout dashboardDate={dashboardDate} />
);

export default DailyRoomStatus;
